import React from 'react';
import Image from 'next/image';
import Link from 'next/link';

import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/fr';
import { useRouter } from 'next/router';
import type { GetServerSidePropsContext } from 'next';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import Sidebar from '@/components/blogs/sidebar';
import * as Icons from '@/components/homepage/Icons';
import styles from '../../components/blogs/blog.module.scss';
import blogs from '@/settings/blogs';
import { REDIRECT_HOME } from '@/lib/common';
import i18n from '@/settings/i18n';
import { formatStoreDate } from '@/lib/intl-format';

dayjs.extend(advancedFormat);
dayjs.extend(utc);

const t = i18n.init('blogs');

// French writes the day before the month with no ordinal ("5 janv. 2024").
// publishedAt is a calendar date at midnight UTC, so format it in UTC or
// timezones behind UTC show the day before.
const formatBlogDate = (date?: string) => {
  const locale = i18n.getLocale();

  const published = dayjs.utc(date);

  return (
    formatStoreDate(published, 'order') ??
    published
      .locale(locale)
      .format(locale === 'fr' ? 'D MMM YYYY' : 'MMM Do YYYY')
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const { params } = context;
  const { handle } = params || {};

  if (!blogs || !blogs?.articles || !handle) {
    return REDIRECT_HOME;
  }

  const article = blogs.articles.find((e) => e.handle === handle);

  if (!article) {
    return { redirect: { permanent: false, destination: '/blogs' } };
  }

  return { props: {} };
};

export default function Blogs() {
  const router = useRouter();

  if (!blogs) {
    return null;
  }

  const { articles } = blogs;

  const { handle } = router.query;
  const article = articles.find((e) => e.handle === handle);
  const articleIndex = articles.findIndex((e) => e.handle === handle);
  const prevArticle = articles[articleIndex - 1];
  const nextArticle = articles[articleIndex + 1];

  return (
    <div>
      <WebsiteSchema
        title={article?.seoTitle ?? article?.title ?? ''}
        description={article?.metaDescription ?? article?.excerpt ?? ''}
      />
      <div className="reebelo-container pb-6 pt-5 md:pb-20 md:pt-8">
        <h1 className="text-left text-base font-semibold text-gray-700 xxs:text-lg xs:text-xl md:text-2xl xl:text-3xl">
          {article?.title}
        </h1>
        <p className="mb-5 text-sm text-gray-700/75 md:text-base">
          {article && article.tags.length > 0 && (
            <>
              <span>{article?.tags[0]} </span>
              <span className="mx-2.5 inline-block h-1.5 w-1.5 rounded-full bg-gray-700">
                {' '}
              </span>
            </>
          )}

          <span>{formatBlogDate(article?.publishedAt)}</span>
        </p>
        <div className="flex flex-wrap gap-5 md:gap-8 lg:flex-nowrap">
          <div className="w-full lg:w-[60%] xl:w-[70%]">
            {article && article.image && (
              <div className="w-full">
                <Image
                  src={article.image}
                  alt={t`Banner`}
                  className="aspect-video w-full rounded-t-lg object-cover"
                  height={535}
                  width={990}
                />
              </div>
            )}

            <div className="hidden items-center rounded-b-lg bg-white p-6 sm:flex">
              <span className="mr-4 text-base text-gray-700"> {t`Share`} </span>
              <ul className="flex items-center gap-3">
                <li>
                  <Link
                    href="https://www.facebook.com/sharer.php?u=https://reebelo.com.au/blogs/reebelo-australia-featured-on-ausbiz"
                    className="inline-block align-middle opacity-40 hover:opacity-100"
                  >
                    <Icons.Facebook />
                  </Link>
                </li>
                <li>
                  <Link
                    href="https://www.pinterest.com/pin/create/button/?url=https://reebelo.com.au/blogs/reebelo-australia-featured-on-ausbiz&media=http://edge.reebelo.com/images/blog/pinterest_share.webp&description=Managing%20Director,%20Gulrez%20Tyebji,%20was%20live%20on%20the%20Startup%20Daily%20show%20on%20ausbiz%20to%20share..."
                    className="inline-block align-middle opacity-40 hover:opacity-100"
                  >
                    <Icons.Pinterest />
                  </Link>
                </li>
                <li>
                  <Link
                    href="https://twitter.com/intent/tweet?text=Reebelo%20Australia%20featured%20on%20AusBiz&url=https%3A%2F%2Freebelo.com.au%2Fblogs%2Fnews%2Freebelo-australia-featured-on-ausbiz"
                    className="inline-block align-middle opacity-40 hover:opacity-100"
                  >
                    <Icons.Twitter />
                  </Link>
                </li>
                <li>
                  <Link
                    href="mailto:?&subject=Reebelo Australia featured on AusBiz&body=https://reebelo.com.au/blogs/reebelo-australia-featured-on-ausbiz"
                    className="inline-block align-middle opacity-40 hover:opacity-100"
                  >
                    <Icons.Email />
                  </Link>
                </li>
              </ul>
            </div>
            <div className="mt-10 block md:mt-14">
              <div className={`${styles.content}`}>
                {article && <div>{article.contentHtml}</div>}
              </div>

              <div className="mb-8 mt-10 flex items-center">
                {article && article.tags.length > 0 && (
                  <>
                    <span className="mr-4 text-sm text-gray-700 sm:text-base">
                      {t`Tags:`}
                    </span>
                    <ul className="flex flex-wrap items-center gap-1">
                      {article?.tags.map((tag) => (
                        <li key={tag}>
                          <Link
                            href={`/blogs?tagged=${tag.toLowerCase()}`}
                            className="inline-block text-sm capitalize text-blue-500 hover:underline sm:text-base"
                          >
                            {tag}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <div className="flex items-center">
                <span className="mr-4 text-sm text-gray-700 sm:text-base">
                  {t`Share`}
                </span>
                <ul className="flex items-center gap-3">
                  <li>
                    <Link
                      href="https://www.facebook.com/sharer.php?u=https://reebelo.com.au/blogs/reebelo-australia-featured-on-ausbiz"
                      className="inline-block align-middle opacity-40 hover:opacity-100"
                    >
                      <Icons.Facebook />
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="https://www.pinterest.com/pin/create/button/?url=https://reebelo.com.au/blogs/reebelo-australia-featured-on-ausbiz&media=http://edge.reebelo.com/images/blog/pinterest_share.webp&description=Managing%20Director,%20Gulrez%20Tyebji,%20was%20live%20on%20the%20Startup%20Daily%20show%20on%20ausbiz%20to%20share..."
                      className="inline-block align-middle opacity-40 hover:opacity-100"
                    >
                      <Icons.Pinterest />
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="https://twitter.com/intent/tweet?text=Reebelo%20Australia%20featured%20on%20AusBiz&url=https%3A%2F%2Freebelo.com.au%2Fblogs%2Fnews%2Freebelo-australia-featured-on-ausbiz"
                      className="inline-block align-middle opacity-40 hover:opacity-100"
                    >
                      <Icons.Twitter />
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="mailto:?&subject=Reebelo Australia featured on AusBiz&body=https://reebelo.com.au/blogs/reebelo-australia-featured-on-ausbiz"
                      className="inline-block align-middle opacity-40 hover:opacity-100"
                    >
                      <Icons.Email />
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="mb-10 mt-12 flex items-center justify-between border-b border-t border-gray-700/10 py-10 md:mt-14">
                <div>
                  {prevArticle && (
                    <Link
                      href={`/blogs/${prevArticle?.handle}`}
                      className="flex items-center text-sm font-bold text-gray-700 hover:text-blue-500"
                    >
                      <svg width="8" height="12" viewBox="0 0 8 12" role="img">
                        <path
                          stroke="currentColor"
                          stroke-width="2"
                          d="M6 10L2 6l4-4"
                          fill="none"
                          stroke-linecap="square"
                        ></path>
                      </svg>
                      <span className="pl-3"> {t`Previous article`} </span>
                    </Link>
                  )}
                </div>
                <div>
                  {nextArticle && (
                    <Link
                      href={`/blogs/${nextArticle?.handle}`}
                      className="flex items-center text-sm font-bold text-gray-700 hover:text-blue-500"
                    >
                      <span className="pr-3"> {t`Next article`} </span>
                      <svg width="8" height="12" viewBox="0 0 8 12" role="img">
                        <path
                          stroke="currentColor"
                          stroke-width="2"
                          d="M2 2l4 4-4 4"
                          fill="none"
                          stroke-linecap="square"
                        ></path>
                      </svg>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="hidden w-full lg:block lg:w-[40%] xl:w-[30%]">
            <Sidebar articles={articles} />
          </div>
        </div>
      </div>
    </div>
  );
}
